import { PasswordField } from '@/react/portainer/gitops/sources/components/PasswordField';
import { UsernameField } from '@/react/portainer/gitops/sources/components/UsernameField';

import { FormSection } from '@@/form-components/FormSection';
import { Icon } from '@@/Icon';

import { AuthTypeOption, Provider } from '../types';

import { providerConfigs } from './providerConfig';
import { AuthTypeSelector } from './AuthTypeSelector';
import { GitProviderSelector } from './GitProviderSelector';

export type { ProviderConfig } from './providerConfig';
export { providerConfigs } from './providerConfig';

type Props = {
  provider: Provider;
  authorizationType: AuthTypeOption;
  username: string;
  password: string;
  isEditing?: boolean;
  errors?: { username?: string; password?: string };
  onChange: (partial: {
    provider?: Provider;
    username?: string;
    password?: string;
    authorizationType?: AuthTypeOption;
  }) => void;
};

export function GitCredentialSection({
  provider,
  authorizationType,
  username,
  password,
  isEditing = false,
  errors,
  onChange,
}: Props) {
  const config = providerConfigs[provider];

  let { passwordLabel } = config;
  if (provider === Provider.Custom) {
    passwordLabel =
      authorizationType === AuthTypeOption.Basic ? 'Password' : 'Access Token';
  }

  return (
    <>
      <FormSection title="Authentication">
        <div className="form-group">
          <div className="col-sm-12 text-muted small">
            Select your Git provider for the best experience, or use Custom for
            other setups.
          </div>
        </div>
        <GitProviderSelector
          value={provider}
          onChange={(value) => {
            const providerConfig = providerConfigs[value];
            onChange({
              provider: value,
              authorizationType:
                providerConfig.fixedAuthType ?? authorizationType,
            });
          }}
        />
      </FormSection>

      <FormSection
        title={
          <div className="flex items-center gap-2">
            <div className="inline-flex h-14 w-14 items-center justify-center text-7xl">
              <Icon icon={config.icon} />
            </div>
            <div>{config.title}</div>
          </div>
        }
      >
        <div className="form-group">
          <div className="col-sm-12 text-muted small">{config.description}</div>
        </div>

        {provider === Provider.Custom && (
          <AuthTypeSelector
            value={authorizationType}
            onChange={(value) => onChange({ authorizationType: value })}
          />
        )}

        <UsernameField
          value={username}
          onChange={(value) => onChange({ username: value })}
          tooltip={config.usernameTooltip}
          error={errors?.username}
        />

        <PasswordField
          value={password}
          onChange={(value) => onChange({ password: value })}
          label={passwordLabel}
          tooltip={config.passwordTooltip}
          error={errors?.password}
          required={!isEditing}
        />
      </FormSection>
    </>
  );
}
